import { Typography } from '@mui/material';
import {
  FC,
  PropsWithChildren,
  SyntheticEvent,
  useCallback,
  useEffect,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { useAppStore } from '../../app/api/app-api';
import { useAppOwners } from '../../application-owners/api/applications-owners-api';
import AppSpinner from '../../common/components/AppSpinner';
import EmptyPageAvatar from '../../common/components/EmptyPageAvatar';
import { AppPages } from '../../common/routing/app-navigator';
import InventoryFiltersStore from '../../dashboard-inventory/stores/inventory-filters-store';
import { useGradualUpdate, useScan } from '../../new-scan/actions/scan-actions';
import {
  initApplication,
  loadApplications,
} from '../actions/applications-actions';
import {
  loadFilters,
  setInitialFilters,
} from '../actions/applications-filters-actions';
import ApplicationsStore from '../stores/applications-store';
import {
  selectApplication,
  clearSelectedApplication,
} from '../stores/applications-store-actions';

const ApplicationsLoader: FC<PropsWithChildren<ApplicationsLoaderProps>> = ({
  children,
  navigateToConnectorsPage,
}) => {
  const { loading, applications } = useSnapshot(ApplicationsStore);
  const { filters } = useSnapshot(InventoryFiltersStore);
  const { displaySelectedAppOwners } = useAppOwners();
  const { isEmptyOrg } = useAppStore();
  const [searchParams] = useSearchParams();
  const filtersApplied = searchParams.get('filters');
  const appId = searchParams.get('appId');

  useEffect(() => {
    initApplication();
  }, []);

  const handleClickConnectors = useCallback(
    (e: SyntheticEvent<HTMLElement>) => {
      e.preventDefault();
      navigateToConnectorsPage();
    },
    [navigateToConnectorsPage],
  );

  const { isCancelScanLoading } = useScan();
  const { inProgress } = useGradualUpdate(
    useCallback(() => {
      loadFilters();
      loadApplications({ update: true, cache: false });
    }, []),
  );

  useEffect(() => {
    appId && selectApplication(appId);
  }, [appId]);

  useEffect(() => clearSelectedApplication, []);
  useEffect(() => {
    setInitialFilters(filtersApplied);
    loadApplications({ update: true });
    loadFilters();
  }, [displaySelectedAppOwners, filters, filtersApplied]);

  if (isCancelScanLoading) {
    return <AppSpinner text='Canceling scan. Please wait...' />;
  }

  if ((loading.isIdle() || loading.isPending()) && !applications) {
    return <AppSpinner text='Loading Applications' />;
  }

  if (inProgress && applications?.length === 0) {
    return (
      <EmptyPageAvatar
        text={`A scan is now running. The first results will appear soon so don't MOOOOve.`}
      />
    );
  }

  if (loading.isError()) {
    return (
      <Typography variant='body1' color='error'>
        Error loading applications list, please try again later
      </Typography>
    );
  }

  if (isEmptyOrg && !inProgress) {
    return (
      <EmptyPageAvatar
        text={
          <>
            Please run a scan to see applications being worked on in your
            organization.
            <br />
            To run a scan...
            <Typography
              variant='body1'
              color='text.primary'
              component='a'
              onClick={handleClickConnectors}
              href={AppPages.Connectors}
              mt={1}
              style={{ display: 'block', fontWeight: 'bold', color: '#FFF' }}>
              Connect your Git projects
            </Typography>
          </>
        }
      />
    );
  }

  return <>{children}</>;
};

interface ApplicationsLoaderProps {
  navigateToConnectorsPage(): void;
}

export default ApplicationsLoader;
